import { __read } from "tslib";
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDateFormatter } from '../../libs/i18n';
import { EXTRA_STEP } from './constants';
import { DateComponents } from './utils/DateComponents';
import { DateTimeFieldAdapter } from './utils/DateTimeFieldAdapter';
export function useDateTimeFieldState(props) {
    var _a = props.value, propValue = _a === void 0 ? null : _a, propMin = props.min, propMax = props.max, propPlaceholder = props.placeholder, formatOptions = props.formatOptions, disabled = props.disabled, readOnly = props.readOnly, onChange = props.onChange;
    var lastValueChange = useRef(null);
    var formatter = useDateFormatter(formatOptions);
    var adapter = useMemo(function () {
        return new DateTimeFieldAdapter({
            formatter: formatter,
            min: propMin,
            max: propMax,
            placeholder: propPlaceholder,
        });
    }, [formatter, propMax, propMin, propPlaceholder]);
    var _b = __read(useState(function () {
        return adapter.normalizeDateComponents(DateComponents.from(propValue));
    }), 2), localValue = _b[0], setLocalValue = _b[1];
    var value = useMemo(function () {
        if (lastValueChange.current !== propValue) {
            return DateComponents.from(propValue);
        }
        return localValue;
    }, [propValue, localValue]);
    lastValueChange.current = propValue;
    var segments = useMemo(function () { return adapter.getSegments(value); }, [adapter, value]);
    var setSegmentValue = useCallback(function (type, segmentValue) {
        if (disabled || readOnly) {
            return;
        }
        var newLocalValue = adapter.setSegmentValue(value, type, segmentValue);
        if (DateComponents.isEqual(value, newLocalValue)) {
            return;
        }
        setLocalValue(newLocalValue);
        var date = adapter.toDate(newLocalValue);
        if (date !== lastValueChange.current) {
            onChange === null || onChange === void 0 ? void 0 : onChange({ value: date });
        }
        lastValueChange.current = date;
    }, [disabled, readOnly, adapter, value, onChange]);
    var stepHandler = useCallback(function (type, step) {
        var segmentValue = adapter.moveByStep(value, type, step);
        setSegmentValue(type, segmentValue);
    }, [adapter, value, setSegmentValue]);
    var increment = useCallback(function (type) { return stepHandler(type, 1); }, [stepHandler]);
    var decrement = useCallback(function (type) { return stepHandler(type, -1); }, [stepHandler]);
    var extraIncrement = useCallback(function (type) { return stepHandler(type, EXTRA_STEP[type] || 1); }, [stepHandler]);
    var extraDecrement = useCallback(function (type) { return stepHandler(type, -(EXTRA_STEP[type] || 1)); }, [stepHandler]);
    var incrementToMax = useCallback(function (type) {
        var max = adapter.getLimits(value, type).max;
        setSegmentValue(type, max);
    }, [adapter, setSegmentValue, value]);
    var decrementToMin = useCallback(function (type) {
        var min = adapter.getLimits(value, type).min;
        setSegmentValue(type, min);
    }, [adapter, setSegmentValue, value]);
    return {
        resolvedOptions: adapter.resolvedOptions,
        segments: segments,
        setSegmentValue: setSegmentValue,
        increment: increment,
        decrement: decrement,
        extraIncrement: extraIncrement,
        extraDecrement: extraDecrement,
        incrementToMax: incrementToMax,
        decrementToMin: decrementToMin,
    };
}
